"use client";

import * as React from "react";
import { toast } from "sonner";

import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useUpdateAgency } from "@/hooks/use-agencies";
import type { Agency, UpdateAgencyDto } from "@/types";

interface EditAgencyDialogProps {
  agency: Agency;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSuccess?: () => void;
}

/**
 * Rename an agency.
 *
 * ⚠️ The Monday.com token field AND the clear-token control that sat beside it
 * are both gone (S22-05-05), and they had to go together. Between them they
 * offered three intents — leave, replace, clear — so keeping either one alone
 * would leave a console that can clear a credential it can no longer set.
 *
 * An agency now manages its own token from its organisation settings, where it
 * is checked against Monday before being stored. Tokens set here previously
 * keep working and keep posting feedback; nothing about stored tokens changed.
 * The backend still accepts the field on this route so that this could ship
 * without a coordinated deploy — removing it there is a later ticket.
 */
export function EditAgencyDialog({
  agency,
  open,
  onOpenChange,
  onSuccess,
}: EditAgencyDialogProps) {
  const [name, setName] = React.useState(agency.name);

  const mutation = useUpdateAgency();

  // Update form data when the agency changes
  React.useEffect(() => {
    setName(agency.name);
  }, [agency]);

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    const trimmedName = name.trim();
    if (!trimmedName) {
      toast.error("Agency name is required");
      return;
    }

    // `mondayToken` is deliberately never sent from this console. On this
    // endpoint an OMITTED key means "leave the stored token alone", which is
    // exactly the intent now that the agency owns its own credential.
    const data: UpdateAgencyDto = { name: trimmedName };

    mutation.mutate(
      { agencyId: agency.id, data },
      {
        onSuccess: (updated) => {
          toast.success(`Agency "${updated.name}" updated successfully!`);
          onOpenChange(false);
          onSuccess?.();
        },
        onError: (error: unknown) => {
          const errorMessage =
            error instanceof Error ? error.message : "Failed to update agency";
          toast.error(errorMessage);
          console.error("Error updating agency:", error);
        },
      },
    );
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[600px]">
        <DialogHeader>
          <DialogTitle>Edit Agency</DialogTitle>
          <DialogDescription>
            Update the agency details below.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit}>
          <div className="grid gap-4 py-4">
            {/* Agency Name */}
            <div className="grid gap-2">
              <Label htmlFor="edit-agency-name">
                Agency Name <span className="text-destructive">*</span>
              </Label>
              <Input
                id="edit-agency-name"
                placeholder="e.g., Awestruck"
                value={name}
                onChange={(e) => setName(e.target.value)}
                required
              />
            </div>
          </div>
          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              disabled={mutation.isPending}
            >
              Cancel
            </Button>
            <Button type="submit" disabled={mutation.isPending}>
              {mutation.isPending ? "Updating..." : "Update Agency"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
